import { useCallback, useMemo } from 'react';

import { useAnalyticsContext } from './context';

type TrackFn = (eventName: string, data: Record<string, unknown>) => void;

type LinksTracker = {
  detailViewed: (params: {
    linkId: string;
    linkName?: string | null;
    institutionId?: string | null;
    mode?: string | null;
    status?: string | null;
  }) => void;

  credentialsEdited: (params: {
    linkId: string;
    linkName?: string | null;
    mode?: string | null;
  }) => void;

  descriptionEdited: (params: {
    linkId: string;
    linkName?: string | null;
  }) => void;

  refreshed: (params: {
    linkId: string;
    linkName?: string | null;
    mode?: string | null;
    typeRequest: 'REFRESH' | 'MONTHLY' | 'HISTORIC';
  }) => void;

  historyRequested: (params: {
    linkId: string;
    linkName?: string | null;
    mode?: string | null;
  }) => void;

  accountsRecovered: (params: {
    linkId: string;
    linkName?: string | null;
    mode?: string | null;
    status?: string | null;
  }) => void;

  transactionsRecategorized: (params: {
    linkId: string;
    linkName?: string | null;
    status?: string | null;
  }) => void;

  transactionSummaryRegenerated: (params: {
    linkId: string;
    linkName?: string | null;
    status?: string | null;
  }) => void;

  toggledEnabled: (params: {
    linkId: string;
    linkName?: string | null;
    newStatus: string;
    previousStatus?: string | null;
  }) => void;

  deleted: (params: {
    linkId: string;
    linkName?: string | null;
    institutionId?: string | null;
    mode?: string | null;
  }) => void;

  createOpened: () => void;

  created: (params: {
    linkId?: string | null;
    institutionId?: string | null;
    mode?: string | null;
  }) => void;
};

export const useLinksTracking = (): LinksTracker => {
  const analytics = useAnalyticsContext();

  const track: TrackFn = useCallback((eventName, data) => {
    try {
      analytics?.mixPanel?.trackContinueEvent?.({
        customEvent: eventName,
        module: 'links',
        ...data,
      });
    } catch {
      // Tracking must never break the UI.
    }
  }, [analytics]);

  return useMemo<LinksTracker>(() => ({
    detailViewed: (params) => track('link_detail_viewed', params),
    credentialsEdited: (params) => track('link_credentials_edited', params),
    descriptionEdited: (params) => track('link_description_edited', params),
    refreshed: (params) => track('link_refreshed', params),
    historyRequested: (params) => track('link_history_requested', params),
    accountsRecovered: (params) => track('link_accounts_recovered', params),
    transactionsRecategorized: (params) => track('link_transactions_recategorized', params),
    transactionSummaryRegenerated: (params) => track('link_transaction_summary_regenerated', params),
    toggledEnabled: (params) => track('link_toggled_enabled', params),
    deleted: (params) => track('link_deleted', params),
    createOpened: () => track('link_create_opened', {}),
    created: (params) => track('link_created', params),
  }), [track]);
};
